import React, { type MouseEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import Tooltip from '@atlaskit/tooltip';
import { LinkButton } from '@atlaskit/button/new';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { BottomProductControlProps } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types.tsx';
import ServicedeskPortalIdProvider from '@atlassian/jira-project-settings-apps-servicedesk-portal-provider/src/index.tsx';
import { viewButton } from '@atlassian/jira-project-settings-product-features-jsm-common/src/common/messages.tsx';
import { usePortalId } from '@atlassian/jira-servicedesk-portal-hook/src/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { PACKAGE_NAME } from '../../constants.tsx';

const ViewButton = ({
	rootContainerId,
	projectId,
	didUserChangeAnything,
}: BottomProductControlProps) => {
	const intl = useIntl();
	const sendAnalytics = (event: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'textField clicked', 'viewIssueLayout');
	};

	return (
		<ServicedeskPortalIdProvider projectId={projectId}>
			{({ servicedeskPortalId }) =>
				// @ts-expect-error - TS2322 - Type 'false | Element' is not assignable to type 'ReactElement<any, string | JSXElementConstructor<any>>'.
				servicedeskPortalId !== undefined &&
				// eslint-disable-next-line jira/ff/no-preconditioning
				(fg('viewed_portal_event_check_for_cortops') &&
				expValEquals('_connecting_request_types_on_portal', 'cohort', 'variation') ? (
					<LinkButton
						isDisabled={didUserChangeAnything}
						onClick={sendAnalytics}
						iconAfter={LinkExternalIcon}
						href={`/servicedesk/customer/portal/${servicedeskPortalId}/create/${rootContainerId}`}
						target="_blank"
						testId="project-settings-product-features-jsm-tmp.ui.view-button.portal-id-cortops"
					>
						{intl.formatMessage(viewButton.viewInPortal)}
					</LinkButton>
				) : (
					<Button
						isDisabled={didUserChangeAnything}
						onClick={sendAnalytics}
						href={`/servicedesk/customer/portal/${servicedeskPortalId}/create/${rootContainerId}`}
						target="_blank"
						testId="project-settings-product-features-jsm-tmp.ui.view-button.portal-id"
					>
						{intl.formatMessage(viewButton.view)}
					</Button>
				))
			}
		</ServicedeskPortalIdProvider>
	);
};

const ViewButtonActual = (props: BottomProductControlProps) => {
	const intl = useIntl();
	const { rootContainerId: requestTypeId, projectKey, didUserChangeAnything } = props;
	const sendAnalytics = (event: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'textField clicked', 'viewIssueLayout');
	};
	const { portalId, portalUrl } = usePortalId({
		projectKey,
		requestTypeId,
	});
	const showButton = portalId && portalUrl;

	if (
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('viewed_portal_event_check_for_cortops') &&
		expValEquals('_connecting_request_types_on_portal', 'cohort', 'variation')
	) {
		return showButton ? (
			<LinkButton
				isDisabled={didUserChangeAnything}
				onClick={sendAnalytics}
				href={`${portalUrl}/create/${requestTypeId}`}
				iconAfter={LinkExternalIcon}
				target="_blank"
				testId="project-settings-product-features-jsm-tmp.ui.view-button.portal-url-cortops"
			>
				{intl.formatMessage(viewButton.viewInPortal)}
			</LinkButton>
		) : (
			<ViewButton {...props} />
		);
	}

	return showButton ? (
		<Button
			isDisabled={didUserChangeAnything}
			onClick={sendAnalytics}
			href={`${portalUrl}/create/${requestTypeId}`}
			target="_blank"
			testId="project-settings-product-features-jsm-tmp.ui.view-button.portal-url"
		>
			{intl.formatMessage(viewButton.view)}
		</Button>
	) : (
		<ViewButton {...props} />
	);
};

export type ViewButtonWrapperProps = BottomProductControlProps & { tooltipDelay?: number };
export const ViewButtonWrapper = (props: ViewButtonWrapperProps) => {
	const intl = useIntl();
	const { projectKey, didUserChangeAnything, tooltipDelay } = props;

	if (projectKey === undefined || projectKey === '') {
		fireErrorAnalytics({
			meta: {
				id: 'jsmViewButton.projectKeyReadError.tmp',
				packageName: PACKAGE_NAME,
				teamName: 'jsd-shield',
			},
		});

		return didUserChangeAnything ? (
			<Tooltip
				content={intl.formatMessage(viewButton.viewDisabledTooltip)}
				position="mouse"
				delay={tooltipDelay}
			>
				<ViewButton {...props} />
			</Tooltip>
		) : (
			<ViewButton {...props} />
		);
	}

	return didUserChangeAnything ? (
		<Tooltip
			content={intl.formatMessage(viewButton.viewDisabledTooltip)}
			position="mouse"
			delay={tooltipDelay}
		>
			<ViewButtonActual {...props} />
		</Tooltip>
	) : (
		<ViewButtonActual {...props} />
	);
};
